import { useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
} from "@mui/material";
import { Add as AddIcon, Save as SaveIcon } from "@mui/icons-material";
import useItemDraft from "../../hooks/useItemDraft";
import InitiativeFormFields from "./InitiativeFormFields";
import {
  createDraft,
  isItemComplete,
  normalizeItem,
  INITIATIVE_FIELDS,
} from "../../domain/initiativeFields";

export default function InitiativeFormDialog({
  open,
  onClose,
  onSubmit,
  initiative,
}) {
  const isEditModeRef = useRef(Boolean(initiative));
  if (open) isEditModeRef.current = Boolean(initiative);
  const isEditMode = isEditModeRef.current;

  const styles = {
    form: { mt: 0.5 },
    title: {
      color: "background.text",
      fontWeight: 700,
      position: "relative",
      pr: 5,
    },
    background: {
      bgcolor: "background.dialog",
      color: "background.text",
    },
    actions: { px: 3, py: 2 },
    button: { textTransform: "none" },
  };

  const { draft, setDraft, setField, addToField, removeFromField } =
    useItemDraft(() => createDraft(initiative ?? {}));

  useEffect(() => {
    if (open) setDraft(createDraft(initiative ?? {}));
  }, [open, initiative, setDraft]);

  const canSave = isItemComplete(draft);

  const handleSubmit = () => {
    if (canSave) {
      onSubmit(normalizeItem(draft));
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: styles.background } }}
    >
      <DialogTitle sx={styles.title}>
        {isEditMode ? "Edit Initiative" : "New Initiative"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={styles.form}>
          <TextField
            autoFocus
            fullWidth
            required
            label="Title"
            value={draft.title}
            onChange={(e) => setField("title", e.target.value)}
            helperText={
              draft.title.length >= INITIATIVE_FIELDS.title.maxLength
                ? `Character limit reached (${INITIATIVE_FIELDS.title.maxLength})`
                : undefined
            }
            slotProps={{
              htmlInput: { maxLength: INITIATIVE_FIELDS.title.maxLength },
            }}
          />
          <TextField
            fullWidth
            required
            multiline
            minRows={3}
            label="Description"
            value={draft.description}
            onChange={(e) => setField("description", e.target.value)}
            helperText={
              draft.description.length >=
              INITIATIVE_FIELDS.description.maxLength
                ? `Character limit reached (${INITIATIVE_FIELDS.description.maxLength})`
                : undefined
            }
            slotProps={{
              htmlInput: { maxLength: INITIATIVE_FIELDS.description.maxLength },
            }}
          />

          <Divider />

          <InitiativeFormFields
            draft={draft}
            setField={setField}
            addToField={addToField}
            removeFromField={removeFromField}
            originalProgress={
              initiative?.progress ?? INITIATIVE_FIELDS.progress.default
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button onClick={onClose} sx={styles.button}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={isEditMode ? <SaveIcon /> : <AddIcon />}
          disabled={!canSave}
          onClick={handleSubmit}
          sx={styles.button}
        >
          {isEditMode ? "Save Changes" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
